import {
  Component,
  Input,
  SimpleChanges,
  inject,
  input,
  output,
  OnInit,
  OnChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { Schedule } from 'src/app/model/contacts/contact-list-schedule';
import { emptySchedule } from 'src/app/utils/utils';
import { ContactList } from 'src/app/model/contacts/contact-list';
import { Contact } from 'src/app/model/contacts/contact';
import { ContactListApiService } from 'src/app/services/api/contact-list-api.service';
import { ConfirmationService, MessageService } from '@openng/optimus-ui/api';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { isNullOrEmpty } from 'src/app/core/functions/common-functions';
import moment from 'moment';
import { timezones } from 'src/app/constants/timezones';
import { Dialog } from '@openng/optimus-ui/dialog';
import { DatePicker } from '@openng/optimus-ui/datepicker';
import { Select } from '@openng/optimus-ui/select';
import { ProgressSpinnerComponent } from '../../../core/components/progress-spinner.component';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-schedule-form',
  templateUrl: './schedule-form.component.html',
  styleUrls: ['./schedule-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    Dialog,
    FormsModule,
    ReactiveFormsModule,
    DatePicker,
    Select,
    ProgressSpinnerComponent,
    ButtonDirective,
    TranslatePipe,
  ],
})
export class ScheduleFormComponent implements OnInit, OnChanges {
  private _contactListAPI = inject(ContactListApiService);
  private _confirmationService = inject(ConfirmationService);
  private _logAndMessage = inject(LogAndMessageService);
  protected _translateService = inject(TranslateService);

  @Input() isVisible: boolean = false;
  readonly scheduleToEdit = input<Schedule>(emptySchedule);
  @Input() formType: string = 'Create';
  @Input() isLoading: boolean = false;
  readonly minDateTime = input.required<Date>();
  readonly contactList = input<ContactList>();
  @Input() disableDelete: boolean = false;
  @Input() contacts: Contact[] = [];

  readonly scheduleFormHidden = output();
  readonly scheduleFormSubmitted = output<any>();
  readonly scheduleDeleted = output<string>();

  timeZones!: { label: string; value: string }[];

  selectedPrimary!: string;
  selectedSecondary!: string;

  id: string = '';
  contactListSchedules: Schedule[] = [];
  viewLoading: boolean = false;
  saving = false;

  scheduleFormGroup = new UntypedFormGroup(
    {
      startTime: new UntypedFormControl(new Date(), Validators.required),
      endTime: new UntypedFormControl(new Date(), Validators.required),
      scheduleTimeZone: new UntypedFormControl('', Validators.required),
      primary: new UntypedFormControl(null),
      secondary: new UntypedFormControl(null),
    },
    { validators: [this.contactValidator(), this.dateValidator()] }
  );

  contactValidator(): ValidatorFn {
    return (
      scheduleFormGroup: AbstractControl<any, any>
    ): ValidationErrors | null => {
      const primary = scheduleFormGroup.get('primary')?.value;
      const secondary = scheduleFormGroup.get('secondary')?.value;

      if (
        primary === secondary &&
        !isNullOrEmpty(primary) &&
        !isNullOrEmpty(secondary)
      ) {
        return { match: true };
      }

      const error =
        isNullOrEmpty(primary) && isNullOrEmpty(secondary)
          ? { required: true }
          : null;
      return error;
    };
  }

  dateValidator(): ValidatorFn {
    return (
      scheduleFormGroup: AbstractControl<any, any>
    ): ValidationErrors | null => {
      const startTime = scheduleFormGroup.get('startTime')?.value;
      const endTime = scheduleFormGroup.get('endTime')?.value;
      const today = new Date();

      if (startTime < today && this.formType != 'Edit') {
        return { start: true };
      }

      if (endTime <= startTime) {
        return { end: true };
      }

      return null;
    };
  }

  ngOnInit(): void {
    this.prepareScheduleForm();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.contactList) {
      this.loadContacts();
    }
  }

  loadTimeZones() {
    this.timeZones = [...timezones];

    this.scheduleFormGroup.patchValue({
      scheduleTimeZone: this.timeZones.find((defaultZone) =>
        defaultZone.value?.includes('New_York')
      ),
    });
  }

  loadContacts() {
    this.contacts =
      this.contactList()?.members.map((member) => member!.contact as Contact) ??
      [];
  }

  prepareScheduleForm() {
    this.loadTimeZones();
    this.loadContacts();
  }

  onDialogHide($event: any) {
    this.scheduleFormHidden.emit();
    // clear out the form data, so we don't have it open back up with a note partially filled in
    // if the user closes it and opens up a new schedule on another note
    setTimeout(() => {
      this.saving = false;
      this.scheduleFormGroup.markAsPristine();
      this.scheduleFormGroup.enable();
      this.disableDelete = false;
      this.scheduleFormGroup.patchValue({
        startTime: new Date(),
        endTime: new Date(),
        primary: null,
        secondary: null,
      });
    }, 50); // delay 50ms to make sure the dialog is closed before we clear it
  }

  onSubmit($event: any) {
    if (!this.scheduleFormGroup.valid) {
      // failsafe to prevent inspect element from making the submit button enabled
      console.warn('Form was somehow submitted without being valid!');
      return;
    }

    if (this.formType === 'Create') {
      this.createContactListSchedule();
    } else if (this.formType === 'Edit') {
      this.updateContactListSchedule($event);
    }
  }

  onTimeZoneChange($event: any) {
    this.scheduleFormGroup.patchValue({
      scheduleTimeZone: $event.value,
    });
  }

  onPrimaryContactChange($event: any) {
    this.selectedPrimary = $event.value;
    this.scheduleFormGroup.patchValue({
      primary: this.selectedPrimary,
    });
  }

  onSecondaryContactChange($event: any) {
    this.selectedSecondary = $event.value;
    this.scheduleFormGroup.patchValue({
      secondary: this.selectedSecondary,
    });
  }

  createContactListSchedule() {
    this.saving = true;
    let addSubmittedSchedule = this.scheduleFormGroup.value;
    const startTimeMoment = moment(addSubmittedSchedule.startTime);
    const convertedStart = moment.tz(
      startTimeMoment.format('YYYY-MM-DDTHH:mm:ss'),
      addSubmittedSchedule.scheduleTimeZone.value
    );
    const endTimeMoment = moment(addSubmittedSchedule.endTime);
    const convertedEnd = moment.tz(
      endTimeMoment.format('YYYY-MM-DDTHH:mm:ss'),
      addSubmittedSchedule.scheduleTimeZone.value
    );
    if (addSubmittedSchedule.primary) {
      let newSchedule = {
        id: '',
        contactId: addSubmittedSchedule.primary.id,
        contactListId: this.contactList()!.id,
        startTime: convertedStart.unix(),
        endTime: convertedEnd.unix(),
        timezone: addSubmittedSchedule.scheduleTimeZone.value,
        isPrimary: true,
      };

      let eventObj = {
        ...newSchedule,
        actualStartTime: addSubmittedSchedule.startTime,
        actualEndTime: addSubmittedSchedule.endTime,
        contact: {
          firstName: addSubmittedSchedule.primary.firstName,
          lastName: addSubmittedSchedule.primary.lastName,
          id: addSubmittedSchedule.primary.id,
        },
      };
      this._contactListAPI.createContactListSchedule(newSchedule).subscribe({
        next: (schedule) => {
          if (schedule.id) {
            this.isVisible = false;
            this.isLoading = false;
            this.scheduleFormSubmitted.emit({
              ...eventObj,
              id: schedule.id,
            });
          } else {
            console.warn('Succeeded without getting an id back?');
            this.isVisible = false;
            this.isLoading = false;
          }
        },
        error: (error) => {
          console.error(error);
          this.isLoading = false;
          this.saving = false;
        },
      });
    }

    if (addSubmittedSchedule.secondary) {
      let newSchedule = {
        id: '',
        contactId: addSubmittedSchedule.secondary.id,
        contactListId: this.contactList()!.id,
        startTime: convertedStart.unix(),
        endTime: convertedEnd.unix(),
        timezone: addSubmittedSchedule.scheduleTimeZone.value,
        isPrimary: false,
      };
      let eventObj = {
        ...newSchedule,
        actualStartTime: addSubmittedSchedule.startTime,
        actualEndTime: addSubmittedSchedule.endTime,
        contact: {
          firstName: addSubmittedSchedule.secondary.firstName,
          lastName: addSubmittedSchedule.secondary.lastName,
          id: addSubmittedSchedule.secondary.id,
        },
      };
      this._contactListAPI.createContactListSchedule(newSchedule).subscribe({
        next: (schedule) => {
          if (schedule.id) {
            this.isVisible = false;
            this.scheduleFormSubmitted.emit({
              ...eventObj,
              id: schedule.id,
            });
            this.isLoading = false;
          } else {
            console.warn('Succeeded without getting an id back?');
            this.isVisible = false;
            this.isLoading = false;
          }
        },
        error: (error) => {
          console.error(error);
          this.isLoading = false;
          this.saving = false;
        },
      });
    }
  }

  async deleteContactListSchedule($event: any) {
    const today = new Date();
    let deletingSchedule = this.scheduleFormGroup.value;
    const endDate = deletingSchedule.endTime;
    if (endDate >= today) {
      this.id = this.scheduleToEdit().id;
      const message = await firstValueFrom(
        this._translateService.get(
          'CONTACT.MESSAGES.CONFIRMATION.DELETE_SCHEDULE'
        )
      );
      this._confirmationService.confirm({
        message: message,
        accept: () => {
          this.saving = true;
          this._contactListAPI
            .deleteContactListSchedule(this.id ?? '')
            .subscribe({
              next: (result) => {
                this.isVisible = false;
                this.scheduleDeleted.emit(this.id);
                setTimeout(() => {
                  this.viewLoading = false;
                }, 50);
              },
              error: (error) => {
                console.error(error);
                this.viewLoading = false;
                this.saving = false;
              },
            });
        },
      });
    } else {
      this.scheduleFormHidden.emit();
      this._logAndMessage.translateToErrorAlert({
        headerKey: 'CONTACT.MESSAGES.HEADERS.DELETE_SCHEDULE',
        bodyKey: 'CONTACT.MESSAGES.WARN.DELETE_PAST_SCHEDULE',
      });
    }
  }

  updateContactListSchedule($event: any) {
    this.saving = true;
    const today = new Date();
    let editingSchedule = this.scheduleFormGroup.value;
    const startTime = this.scheduleFormGroup.get('startTime')?.value;
    const startTimeValueMoment = moment(startTime);
    const convertedStartTimeValueMoment = moment.tz(
      startTimeValueMoment.format('YYYY-MM-DDTHH:mm:ss'),
      editingSchedule.scheduleTimeZone.value
    );
    const startTimeMoment = moment(editingSchedule.startTime);
    const convertedStart = moment.tz(
      startTimeMoment.format('YYYY-MM-DDTHH:mm:ss'),
      editingSchedule.scheduleTimeZone.value
    );
    const endTimeMoment = moment(editingSchedule.endTime);
    const convertedEnd = moment.tz(
      endTimeMoment.format('YYYY-MM-DDTHH:mm:ss'),
      editingSchedule.scheduleTimeZone.value
    );

    let newSchedule = {
      id: this.scheduleToEdit().id,
      contactId: editingSchedule.primary
        ? editingSchedule.primary.id
        : editingSchedule.secondary.id,
      contactListId: this.contactList()!.id,
      startTime: convertedStartTimeValueMoment.unix(),
      endTime: convertedEnd.unix(),
      timezone: editingSchedule.scheduleTimeZone.value,
      isPrimary: editingSchedule.primary ? true : false,
      version: editingSchedule.version,
    };

    if (startTime > today) {
      newSchedule.startTime = convertedStart.unix();
    }

    let contactInformation = {
      contact: {
        firstName: '',
        lastName: '',
        id: '',
      },
    };

    if (editingSchedule.primary) {
      contactInformation = {
        contact: {
          firstName: editingSchedule.primary.firstName,
          lastName: editingSchedule.primary.lastName,
          id: editingSchedule.primary.id,
        },
      };
    } else if (editingSchedule.secondary) {
      contactInformation = {
        contact: {
          firstName: editingSchedule.secondary.firstName,
          lastName: editingSchedule.secondary.lastName,
          id: editingSchedule.secondary.id,
        },
      };
    }

    let eventObj = {
      ...newSchedule,
      actualStartTime: editingSchedule.startTime,
      actualEndTime: editingSchedule.endTime,
      ...contactInformation,
    };

    this._contactListAPI.updateContactListSchedule(newSchedule).subscribe({
      next: (schedule) => {
        if (schedule.id) {
          this.isVisible = false;
          let oldIndex = this.contactListSchedules!.findIndex(
            (x) => x.id === newSchedule.id
          );
          this.contactListSchedules?.splice(oldIndex, 1, schedule);
          this.scheduleFormSubmitted.emit(eventObj);
          this.isLoading = false;
        } else {
          console.warn('Somehow succeeded without getting an id back?');
          this.isVisible = false;
          this.isLoading = false;
        }
      },
      error: (error) => {
        console.error(error);
        this.isLoading = false;
        this.saving = false;
      },
    });
  }
}
